import React, { ChangeEvent } from 'react'
import styled from 'styled-components'
import { NavLink } from 'react-router-dom'

import Textarea from 'components/Textarea'
import TextInput from 'components/TextInput'
import LabelledField from 'components/LabelledField'
import RadioInputs from 'components/RadioInputs'

import FormSection from 'pages/shared/FormSection'

import { color } from 'styles/colors'

import { scrubText } from 'helpers/inputCleaners'
import { DropzoneArea } from 'mui-file-dropzone'

type MessageFieldsErrors = {
  title?: string
  message?: string
}

type MessageFieldsProps = {
  imageType: string
  onChangeCallback: (update: { imageType?: string; image?: string | null; title?: string; message?: string }) => void
  title?: string
  message?: string
  image?: string
  errors?: MessageFieldsErrors
}

const MessageFields = ({ imageType, image = '', title = '', message = '', onChangeCallback, errors = {} }: MessageFieldsProps) => {
  const onFileChange = (files: File[]) => {
    const file = files[0]
    if (!file) return
    const reader = new FileReader()

    reader.onload = () => {
      onChangeCallback({ image: typeof reader.result === 'string' ? reader.result : null })
    }

    reader.readAsDataURL(file)
  }

  return (
    <FormSection title="Image">
      <LabelledField label="Image Type" id="image-type">
        <RadioInputs
          selectedValue={imageType}
          radioOptions={[
            { label: 'Image', value: 'image' },
            { label: 'Icon', value: 'icon' }
          ]}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChangeCallback({ imageType: event.target.value })}
        />
      </LabelledField>

      {
        !image &&
        <DropzoneArea
          acceptedFiles={['image/jpeg', 'image/png', 'image/bmp', 'image/gif']}
          filesLimit={1}
          fileObjects={[]}
          dropzoneText={'Drag and drop an image (jpeg, jpg, png, bmp, gif) here or click'}
          onDrop={files => onFileChange(files as File[])}
        />
      }

      {
        image &&
        <S.RemoveImage>
          <NavLink to="#" onClick={() => onChangeCallback({ image: null })}>Remove image</NavLink>
          <S.Image id="image" alt="select" src={image} {...{ imageType }} />
        </S.RemoveImage>
      }

      <TextInput
        label="Title"
        isRequired
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChangeCallback({ title: scrubText(event.target.value, true) })}
        value={title || ''}
        errorMessage={errors.title}
      />

      <Textarea
        label="Message"
        isRequired
        rows={10}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChangeCallback({ message: scrubText(event.target.value, true) })}
        value={message || ''}
        errorMessage={errors.message}
      />
    </FormSection>
  )
}

export default MessageFields

const S = {
  RemoveImage: styled.div`
    display: flex;
    flex-direction: column;
    max-width: 750px;
  `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['imageType'].includes(prop)
  })<{ imageType: string }>`
    border: 1px solid ${color('border')};
    ${({ imageType }) => imageType === 'icon' ? 'width: 64px;' : 'width: max-content;'}
  `,
  RadioInputLabels: `
    border-radius: 4px;
    border: 1px solid;
    border-color: ${color('border')};
    text-align: center;
    padding: 0px 10px;
    font-weight: 900;
    font-size: 12px;
    text-transform: uppercase;
  `
}
